import { fromJS } from 'immutable';
import { NACH_STATUS, NACH_MODE_STRING, NACH_JOURNEY_MODES } from 'constants/enums';

export const NACH_REQUEST_TYPES = ['EMI-1', 'EMI-2', 'SECURITY'];

export const JOURNEY_MODE_ORDER = ['API', 'PHYSICAL', 'NO_NACH_PDC'];

export function modeIntToString(modeInt) {
    return NACH_MODE_STRING.get(modeInt) || null;
}

export function modeStringToInt(modeStr) {
    if (!modeStr) return null;
    const key = modeStr.toLowerCase();
    if (key === 'api') return 1;
    if (key === 'physical') return 2;
    if (key === 'no_nach_pdc') return 3;
    return null;
}

export function journeyModeKeyToInt(modeKey) {
    const entry = NACH_JOURNEY_MODES.get(modeKey);
    return entry ? entry.nach_mode : null;
}

export function getNachStatusLabel(code) {
    if (code == null) return '—';
    let label = '—';
    NACH_STATUS.forEach((value, key) => {
        if (value === code) {
            label = key
                .split('_')
                .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
                .join(' ');
        }
    });
    return label;
}

export function isSuccessStatus(code) {
    return [NACH_STATUS.get('VERIFIED'), NACH_STATUS.get('BANK_ACCEPTED')].includes(code);
}

export function isInProgressStatus(code) {
    return code === NACH_STATUS.get('OPEN');
}

export function canCancelStatus(code) {
    return [
        NACH_STATUS.get('OPEN'),
        NACH_STATUS.get('VERIFIED'),
        NACH_STATUS.get('BANK_ACCEPTED'),
    ].includes(code);
}

export function toPlainJs(value) {
    if (value == null) return value;
    return typeof value.toJS === 'function' ? value.toJS() : value;
}

export function getDefaultMandateTypeFromCategory(category) {
    if (!category) return 1;
    if (category.next_mode_value != null) return category.next_mode_value;
    const modes = category.modes || {};
    if (modes.API && modes.API.can_trigger) return 1;
    if (modes.PHYSICAL && modes.PHYSICAL.can_trigger) return 2;
    if (modes.NO_NACH_PDC && modes.NO_NACH_PDC.can_trigger) return 3;
    return 1;
}

export function resolveTotalEmiCount(journey, resultsList, resultRow, emiListLength) {
    const fromResults = (resultsList || []).reduce((max, row) => {
        const len =
            row && row.term_loan_instalment && row.term_loan_instalment.length
                ? row.term_loan_instalment.length
                : 0;
        return len > max ? len : max;
    }, 0);

    return (
        (journey && journey.emi_count) ||
        fromResults ||
        (resultRow &&
            resultRow.term_loan_instalment &&
            resultRow.term_loan_instalment.length) ||
        emiListLength ||
        1
    );
}

export function findResultForType(results, nachRequestType, emiDate) {
    if (!results || !results.length) return null;
    return (
        results.find(
            (r) =>
                r.nach_details &&
                r.nach_details.nach_request_type === nachRequestType &&
                (emiDate == null || r.nach_details.emi_date === emiDate)
        ) || null
    );
}

export function getCategoryTermLoanInstalments(category) {
    if (!category || !category.term_loan_instalment) return [];
    const list = category.term_loan_instalment;
    return Array.isArray(list) ? list.filter(Boolean) : [];
}

/** term_loan_instalment on category or modes.NO_NACH_PDC (after Generate / OPEN status) */
export function getNoNachPdcTermLoanInstalments(category) {
    if (!category) return [];
    const modes = category.modes || {};
    const mode = modes.NO_NACH_PDC || modes.no_nach_pdc;
    if (mode && Array.isArray(mode.term_loan_instalment) && mode.term_loan_instalment.length) {
        return mode.term_loan_instalment.filter(Boolean);
    }
    return getCategoryTermLoanInstalments(category);
}

/** term_loan_instalment on category or modes.PHYSICAL */
export function getPhysicalTermLoanInstalments(category) {
    if (!category) return [];
    const modes = category.modes || {};
    const mode = modes.PHYSICAL;
    if (mode && Array.isArray(mode.term_loan_instalment) && mode.term_loan_instalment.length) {
        return mode.term_loan_instalment.filter(Boolean);
    }
    return getCategoryTermLoanInstalments(category);
}

export function emiDateFromInstalmentRow(row) {
    if (!row) return null;
    if (row.emi_date != null && row.emi_date !== '') return row.emi_date;
    if (row.due_date != null && row.due_date !== '') return row.due_date;
    return null;
}

/** Single EMI date from nach_journey.categories[EMI-1|EMI-2].emi_date, else first emi_dates entry */
export function getCategoryEmiDate(category) {
    if (!category) return null;
    if (category.emi_date != null && category.emi_date !== '') {
        return category.emi_date;
    }
    const dates = getCategoryEmiDates(category);
    return dates.length ? dates[0] : null;
}

/** EMI dates from nach_journey.categories[EMI-1|EMI-2].emi_dates (e.g. [3] or [16]) */
export function getCategoryEmiDates(category) {
    if (!category) return [];
    const sources = [
        category.emi_dates,
        category.modes && category.modes.API && category.modes.API.emi_dates,
        category.modes && category.modes.PHYSICAL && category.modes.PHYSICAL.emi_dates,
        category.modes && category.modes.NO_NACH_PDC && category.modes.NO_NACH_PDC.emi_dates,
    ];
    for (let i = 0; i < sources.length; i += 1) {
        const src = sources[i];
        if (Array.isArray(src) && src.length) {
            return src.flat().filter((d) => d != null && d !== '');
        }
    }
    return [];
}

/** Resolves emi_date for GET case/{id}/enach-mandate/ query params */
export function resolveEmiDateForNachRequest({
    nachRequestType,
    payloadEmiDate,
    category,
    emiDates = [],
    resultsList = [],
    journey,
    selectedEmiDate,
}) {
    if (payloadEmiDate != null && payloadEmiDate !== '') {
        return payloadEmiDate;
    }
    if (category && category.emi_date != null && category.emi_date !== '') {
        return category.emi_date;
    }

    const categoryEmiDates = getCategoryEmiDates(category);
    if (categoryEmiDates.length) {
        return categoryEmiDates[0];
    }

    const instalments = getCategoryTermLoanInstalments(category);
    if (instalments.length) {
        const idx = nachRequestType === 'EMI-2' ? 1 : 0;
        const fromRow =
            emiDateFromInstalmentRow(instalments[idx]) ||
            emiDateFromInstalmentRow(instalments[0]);
        if (fromRow != null && fromRow !== '') return fromRow;
    }

    const flatEmiDates = (emiDates || []).flat().filter((d) => d != null && d !== '');
    if (flatEmiDates.length) {
        if (nachRequestType === 'EMI-2' && flatEmiDates.length > 1) {
            return flatEmiDates[1];
        }
        return flatEmiDates[0];
    }

    if (selectedEmiDate != null && selectedEmiDate !== '') {
        return selectedEmiDate;
    }

    const resultRow = (resultsList || []).find(
        (r) => r && r.nach_details && r.nach_details.nach_request_type === nachRequestType
    );
    if (resultRow) {
        if (resultRow.nach_details.emi_date != null && resultRow.nach_details.emi_date !== '') {
            return resultRow.nach_details.emi_date;
        }
        const resultInstalments = resultRow.term_loan_instalment;
        if (Array.isArray(resultInstalments) && resultInstalments.length) {
            const idx = nachRequestType === 'EMI-2' ? 1 : 0;
            const fromResult =
                emiDateFromInstalmentRow(resultInstalments[idx]) ||
                emiDateFromInstalmentRow(resultInstalments[0]);
            if (fromResult != null && fromResult !== '') return fromResult;
        }
    }

    if (journey && journey.emi_date != null && journey.emi_date !== '') {
        return journey.emi_date;
    }

    return null;
}

/** Section titles per design mockup */
export function getCategoryDisplayLabel(nachRequestType) {
    if (nachRequestType === 'EMI-1') return 'E-NACH — EMI 1';
    if (nachRequestType === 'EMI-2') return 'E-NACH — EMI 2';
    if (nachRequestType === 'SECURITY') return 'E-NACH — Security';
    return nachRequestType;
}

export function getCategorySubtitle(nachRequestType, journey, emiDates, category) {
    if (nachRequestType === 'SECURITY') return 'Security · 3 cheques';

    const sectionEmiDate = getCategoryEmiDate(category);
    if (sectionEmiDate != null && sectionEmiDate !== '') {
        return `EMI date: ${sectionEmiDate}`;
    }
    return null;
}

/**
 * Design shows EMI + Security for Term Loan cases.
 * SCF (non–term loan) cases show only the Security section.
 * EMI-2 is shown only when instalment_frequency === 1 (fortnightly, 2 EMIs/month);
 * when instalment_frequency === 2 (single EMI/month) only EMI-1 is shown.
 */
export function getDesignSectionTypes(nachJourney, requiredNachTypes, instalmentFrequency) {
    const categories = (nachJourney && nachJourney.categories) || {};
    const isTermLoan = !!(nachJourney && nachJourney.is_term_loan);
    const freq = Number(
        instalmentFrequency != null ? instalmentFrequency : nachJourney && nachJourney.instalment_frequency
    );
    const showEmi2 = freq !== 2;
    const types = [];

    if (isTermLoan) {
        if (categories['EMI-1'] || requiredNachTypes.includes('EMI-1')) types.push('EMI-1');
        if (showEmi2 && (categories['EMI-2'] || requiredNachTypes.includes('EMI-2'))) {
            types.push('EMI-2');
        }
    }

    if (categories.SECURITY || requiredNachTypes.includes('SECURITY')) types.push('SECURITY');

    if (!types.length) {
        if (!isTermLoan) {
            return Object.keys(categories).filter((t) => t === 'SECURITY');
        }
        return Object.keys(categories).filter((t) => showEmi2 || t !== 'EMI-2');
    }
    return types;
}

/**
 * instalment_frequency: 1 = fortnightly (2 EMIs/month), 2 = single EMI/month
 * Physical Mandate cheque rows for EMI-1 / EMI-2: always 3
 * Security Physical: always 3
 */
export function getPhysicalChequeRowCount(nachRequestType) {
    if (nachRequestType === 'SECURITY') return 3;
    if (nachRequestType === 'EMI-1' || nachRequestType === 'EMI-2') return 3;
    return 3;
}

/**
 * No NACH PDC rows:
 * - Security: always 3
 * - EMI-1 / EMI-2 + fortnightly (1): one row per month (totalEmiCount / 2), e.g. 10 EMIs → 5 rows
 * - EMI-1 / EMI-2 + single EMI (2): one row per EMI (totalEmiCount)
 */
export function getNoNachPdcRowCount(nachRequestType, instalmentFrequency, totalEmiCount) {
    if (nachRequestType === 'SECURITY') return 3;

    const total = Number(totalEmiCount) || 0;
    const freq = Number(instalmentFrequency);

    if (freq === 1) {
        return total > 0 ? Math.max(1, Math.floor(total / 2)) : 3;
    }

    if (freq === 2) {
        return total > 0 ? total : 3;
    }

    return total > 0 ? total : 3;
}

/**
 * Existing EML for No NACH PDC: category.has_eml_document + modes.NO_NACH_PDC.eml_document_url
 */
export function getNoNachPdcEmlDocumentUrl(category, resultRow) {
    const modes = (category && category.modes) || {};
    const mode =
        modes.NO_NACH_PDC ||
        modes.no_nach_pdc ||
        modes.No_Nach_Pdc ||
        null;
    let url =
        (mode && (mode.eml_document_url || mode.eml_url)) ||
        (category && (category.eml_document_url || category.eml_url)) ||
        null;

    if ((!url || url === '') && resultRow) {
        const details = resultRow.nach_details || {};
        url = details.eml_document_url || details.eml_url || null;
    }

    if (url == null || url === '') return null;
    return String(url).trim();
}

export function categoryHasNoNachPdcEml(category, resultRow) {
    if (getNoNachPdcEmlDocumentUrl(category, resultRow)) return true;
    return !!(category && category.has_eml_document === true);
}

export function getProceedBlockerLabel(nachRequestType) {
    if (nachRequestType === 'EMI-1' || nachRequestType === 'EMI-2') return 'EMI NACH';
    if (nachRequestType === 'SECURITY') return 'Security NACH';
    return nachRequestType;
}

/** User-facing status labels matching design (Idle, Initiated, etc.) */
export function getDisplayStatusLabel(statusCode, statusLabel) {
    if (statusLabel) return statusLabel;
    if (statusCode == null) return 'Idle';
    const map = {
        1: 'Initiated',
        2: 'Cancelled',
        3: 'Failed',
        4: 'Verified',
        5: 'Bank Confirmed',
    };
    return map[statusCode] || 'Idle';
}

export function getStatusTagColor(statusCode, statusLabel) {
    const label = (statusLabel || getDisplayStatusLabel(statusCode)).toLowerCase();
    if (statusCode === 2 || statusCode === 3 || statusCode === 6 || label.includes('fail') || label.includes('reject')) {
        return 'red';
    }
    if (statusCode === 4 || statusCode === 5 || label.includes('verified') || label.includes('bank confirm')) {
        return 'green';
    }
    if (
        statusCode === 1 ||
        label.includes('pending') ||
        label.includes('initiat') ||
        label.includes('open')
    ) {
        return 'gold';
    }
    if (statusCode == null || label.includes('idle')) {
        return 'blue';
    }
    return 'default';
}

export function buildMandateSummary(category, mandateType, resultRow) {
    if (!category || !category.modes || !mandateType) return null;

    const modeKey =
        mandateType === 1 ? 'API' : mandateType === 2 ? 'PHYSICAL' : 'NO_NACH_PDC';
    const modeJourney = category.modes[modeKey];
    const nachDetails =
        resultRow &&
        resultRow.nach_details &&
        resultRow.nach_details.mandate_type === mandateType
            ? resultRow.nach_details
            : null;

    const modeLabel = NACH_JOURNEY_MODES.get(modeKey);
    const transferred =
        nachDetails &&
        nachDetails.digio_respone &&
        nachDetails.digio_respone.data &&
        nachDetails.digio_respone.data.bank_details &&
        nachDetails.digio_respone.data.bank_details.bank_name;

    let statusLabel = getDisplayStatusLabel(
        modeJourney && modeJourney.status,
        modeJourney && modeJourney.status_label
    );
    if (nachDetails && nachDetails.nach_status != null) {
        statusLabel = getDisplayStatusLabel(nachDetails.nach_status, null);
    }

    return {
        mandate_id: (nachDetails && nachDetails.mandate_id) || '—',
        mode: (modeLabel && modeLabel.label) || '—',
        transferred_to: transferred || '—',
        umrn: (nachDetails && nachDetails.umrn) || '—',
        status: statusLabel,
        statusCode:
            (nachDetails && nachDetails.nach_status) != null
                ? nachDetails.nach_status
                : modeJourney && modeJourney.status,
    };
}

export function buildProceedValidationFromJourney(nachJourney, requiredNachTypes) {
    if (!nachJourney || !requiredNachTypes || !requiredNachTypes.length) {
        return { can_proceed: true, blockers: [], is_ready_message: null };
    }

    const categories = nachJourney.categories || {};
    const blockers = [];

    requiredNachTypes.forEach((type) => {
        const category = categories[type];
        if (!category || !category.modes || !category.modes.API) return;
        const apiMode = category.modes.API;
        const status = apiMode.status;
        if (!isSuccessStatus(status)) {
            const current =
                apiMode.status_label ||
                getNachStatusLabel(status) ||
                'Not started';
            const displayCurrent =
                current === 'Open' || current === 'OPEN' ? 'Idle' : current;
            blockers.push({
                nach_request_type: type,
                mandate_mode: 'API',
                required_status: 'VERIFIED',
                current_status: current,
                message: `${getProceedBlockerLabel(type)}: API Mandate must be Verified (current: ${displayCurrent}).`,
            });
        }
    });

    return {
        can_proceed: blockers.length === 0,
        blockers,
        is_ready_message:
            blockers.length === 0
                ? 'All NACH/PDC requirements satisfied — ready to proceed.'
                : null,
    };
}

/**
 * Normalizes GET /enach-status/ for Redux. Prefer backend nach_journey when present.
 */
export function normalizeEnachStatusResponse(apiData, meta = {}) {
    const data = apiData || {};
    const results = data.results || [];
    const emi_dates = (data.emi_dates || []).flat();
    const bank_account = data.bank_account || {};
    const nach_journey = data.nach_journey || null;
    const nach_records = data.nach_records || {};
    const nach_history = data.nach_history || [];
    const nach_types = data.nach_types || NACH_REQUEST_TYPES;
    const required_nach_types = data.required_nach_types || [];

    const proceed_validation = nach_journey
        ? buildProceedValidationFromJourney(nach_journey, required_nach_types)
        : { can_proceed: false, blockers: [], is_ready_message: null };

    return fromJS({
        bank_account,
        emi_dates,
        results,
        nach_types,
        required_nach_types,
        nach_journey,
        nach_records,
        nach_history,
        proceed_validation,
        instalment_frequency:
            data.instalment_frequency != null
                ? data.instalment_frequency
                : nach_journey && nach_journey.instalment_frequency,
        is_term_loan: nach_journey ? nach_journey.is_term_loan : meta.schemeType === 'Term Loan',
        is_loan_above_1_cr:
            data.is_loan_above_1_cr != null
                ? data.is_loan_above_1_cr
                : isLoanAbove1CrFromSanctionLimit(meta.sanctionLimit),
        is_backend_nach_activation_enabled:
            data.is_backend_nach_activation_enabled != null
                ? data.is_backend_nach_activation_enabled
                : !!nach_journey,
        data: results,
    });
}

export function getVisibleNachTypes(nachJourney, nachTypes, isTermLoan) {
    const types = (nachJourney && nachJourney.nach_types) || nachTypes || NACH_REQUEST_TYPES;
    if (!isTermLoan) {
        return types.filter((t) => t !== 'EMI-1' && t !== 'EMI-2').length
            ? types.filter((t) => t === 'SECURITY' || !t.startsWith('EMI'))
            : ['SECURITY'];
    }
    return types;
}

export function buildModeDropdownOptions(category) {
    if (!category || !category.modes) return [];
    return JOURNEY_MODE_ORDER.map((modeKey) => {
        const m = category.modes[modeKey];
        const mandateType = m.nach_mode || journeyModeKeyToInt(modeKey);
        const hasHistory = m.status != null;
        // A mode is selectable only when the backend allows triggering it,
        // or when it has prior history (so an existing attempt can be viewed).
        const isEnabled = m.can_trigger === true || hasHistory;
        return {
            mandate_type: mandateType,
            value: mandateType,
            label: (NACH_JOURNEY_MODES.get(modeKey) && NACH_JOURNEY_MODES.get(modeKey).label) || modeKey,
            key: modeKey,
            is_enabled: isEnabled,
            is_locked: !isEnabled,
            lock_reason: m.blocked_reason,
            is_default: category.next_allowed_mode === modeKey,
            journey_mode: m,
        };
    });
}

export function getNachMandateDetailValue(nachData, fieldKey) {
    if (!nachData) return undefined;

    const resolveField = (field) => {
        if (field == null || field === '') return undefined;
        const plain = field && field.toJS ? field.toJS() : field;
        if (plain && typeof plain === 'object' && plain.value != null && plain.value !== '') {
            return plain.value;
        }
        if (typeof plain === 'string' || typeof plain === 'number') return plain;
        return undefined;
    };

    const topLevel = resolveField(nachData.get ? nachData.get(fieldKey) : nachData[fieldKey]);
    if (topLevel != null) return topLevel;

    const panels = nachData.get ? nachData.get('data') : nachData.data;
    if (!panels) return undefined;

    const panelValues = panels.valueSeq ? panels.valueSeq().toArray() : Object.values(panels);
    for (const panel of panelValues) {
        const field = panel && panel.get ? panel.get(fieldKey) : panel && panel[fieldKey];
        const value = resolveField(field);
        if (value != null) return value;
    }
    return undefined;
}

const NACH_SECTION_GUIDE_BELOW_1CR = [
    {
        label: 'Overall',
        text: 'API Mandate > Physical mandate > No NACH (only basis approval mail)',
    },
    {
        label: 'Physical mandate',
        text: 'Trigger first and Save 3 cheque details parallelly',
    },
    {
        label: 'No NACH',
        text: 'Upload approval mail first and then click Generate to save cheque details',
    },
];

export function isLoanAbove1CrFromSanctionLimit(sanctionLimit) {
    return Number(sanctionLimit) >= 10000000;
}

export function getNachSectionTooltip(nachRequestType, isTermLoan, isLoanAbove1Cr) {
    const isEmi = nachRequestType === 'EMI-1' || nachRequestType === 'EMI-2';
    const isSecurity = nachRequestType === 'SECURITY';

    if (isEmi && isTermLoan) {
        if (isLoanAbove1Cr) {
            return {
                type: 'single',
                text: 'For EMI amount > 1 Cr, only "No NACH" will be allowed.',
            };
        }
        return { type: 'list', items: NACH_SECTION_GUIDE_BELOW_1CR };
    }

    if (isSecurity) {
        if (isLoanAbove1Cr) {
            return {
                type: 'single',
                text: 'For Sanction Limit > 50 Lakh, only "No NACH" will be allowed.',
            };
        }
        return { type: 'list', items: NACH_SECTION_GUIDE_BELOW_1CR };
    }

    return null;
}
